import { useState, useCallback } from "react";
import { Pressable, Text, Platform } from "react-native";
import { ChevronRight, Check } from "lucide-react-native";
import { EMBER } from "../../onboarding/theme";
import { useInterestCapture } from "../hooks/useInterestCapture";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  featureId: string;
  featureType: "program" | "supplement" | "test" | "product" | "therapy";
  featureName: string;
  label?: string;
  capturedLabel?: string;
};

export default function InterestButton({
  featureId,
  featureType,
  featureName,
  label = "I'm interested",
  capturedLabel = "We'll notify you!",
}: Props) {
  const [captured, setCaptured] = useState(false);
  const { capture } = useInterestCapture();
  const capturePress = usePressCapture("InterestButton");

  const handlePress = useCallback(() => {
    capturePress(
      pressLabel(captured ? capturedLabel : label),
      "register_interest",
    );
    if (captured) {
      return;
    }
    capture({ featureId, featureType, featureName });
    setCaptured(true);
  }, [
    captured,
    capture,
    capturePress,
    capturedLabel,
    label,
    featureId,
    featureType,
    featureName,
  ]);

  return (
    <Pressable
      onPress={handlePress}
      style={
        {
          backgroundColor: captured
            ? `${EMBER.colors.green}18`
            : EMBER.colors.warm,
          paddingVertical: 14,
          paddingHorizontal: 24,
          borderRadius: 12,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          overflow: "hidden",
          ...(Platform.OS === "web" &&
            !captured && {
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
              backgroundSize: "200px 200px, 100% 100%",
            }),
        } as Record<string, unknown>
      }
    >
      {captured ? (
        <Check size={16} color={EMBER.colors.green} strokeWidth={2} />
      ) : (
        <ChevronRight size={16} color="#FFF" strokeWidth={2} />
      )}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "700",
          color: captured ? EMBER.colors.green : "#FFF",
        }}
      >
        {captured ? capturedLabel : label}
      </Text>
    </Pressable>
  );
}
